/**
 * Плотная таблица с постраничной навигацией (AGENTS.md §3.8).
 *
 * Постраничная, а не курсорная: админу нужно понимать, сколько всего записей
 * и уметь вернуться на ту же страницу — в ленте это неважно, здесь важно.
 */
import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface Column<T> {
  key: string;
  title: string;
  render: (row: T) => ReactNode;
  align?: 'right';
  width?: string;
}

interface DataTableProps<T> {
  columns: Array<Column<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  total?: number;
  page?: number;
  totalPages?: number;
  loading?: boolean;
  /** Текст пустой таблицы. По умолчанию — «ничего не найдено». */
  emptyText?: string;
  onPage?: (page: number) => void;
  onRowClick?: (row: T) => void;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  total,
  page = 1,
  totalPages = 1,
  loading,
  emptyText,
  onPage,
  onRowClick,
}: DataTableProps<T>) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto rounded-card border border-line">
        <table className="w-full border-collapse text-meta">
          <thead>
            <tr className="border-b border-line bg-surface-secondary">
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={column.width ? { width: column.width } : undefined}
                  className={`px-3 py-2 font-medium text-content-secondary ${
                    column.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-8 text-center text-content-muted">
                  {loading ? t('common.loading') : (emptyText ?? t('common.nothingFound'))}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`border-b border-line last:border-0 bg-surface ${
                    onRowClick ? 'cursor-pointer hover:bg-surface-secondary' : ''
                  }`}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`px-3 py-2 align-top text-content ${
                        column.align === 'right' ? 'text-right tabular-nums' : ''
                      }`}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {onPage && totalPages > 1 ? (
        <div className="flex items-center justify-between text-meta text-content-secondary">
          <span>
            {t('common.pageOf', { page, total: totalPages })}
            {total !== undefined ? ` · ${t('common.totalItems', { count: total })}` : ''}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => onPage(page - 1)}
              className="rounded-control border border-line p-1.5 text-content-secondary transition-colors duration-fast hover:text-content disabled:opacity-40"
              aria-label={t('common.prevPage')}
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => onPage(page + 1)}
              className="rounded-control border border-line p-1.5 text-content-secondary transition-colors duration-fast hover:text-content disabled:opacity-40"
              aria-label={t('common.nextPage')}
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
